// src/pages/RoomStatusPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Tag,
  Alert,
  Space,
  Modal,
  message,
  Dropdown,
  notification,
  Badge,
} from "antd";
import {
  SearchOutlined,
  HomeOutlined,
  DownOutlined,
  SyncOutlined,
  CloseOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  StopOutlined,
  WarningOutlined,
  QuestionOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";

const { Title, Text } = Typography;

/** Slim / neat styling */
const slimStyles = `
  .rs-page { padding: 16px; }

  .rs-head {
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:12px;
    flex-wrap:wrap;
  }
  .rs-head-title { display:flex; flex-direction:column; gap:2px; }
  .rs-actions { display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }

  .rs-filters { margin-top: 10px; }
  .rs-filters .ant-input { height: 34px; }
  .rs-filters .ant-btn { height: 34px; }

  .rs-table.ant-table-wrapper .ant-table-thead > tr > th{
    padding: 6px 8px !important;
    font-size: 12px !important;
    font-weight: 600;
    background: #fafafa;
    white-space: nowrap;
  }
  .rs-table.ant-table-wrapper .ant-table-tbody > tr > td{
    padding: 6px 8px !important;
    font-size: 12px !important;
    vertical-align: top;
  }
  .rs-table .ant-table-pagination{ margin: 12px 0 !important; }

  .rs-pill{
    margin: 0 !important;
    font-size: 11px !important;
    padding: 0px 8px !important;
    border-radius: 999px !important;
    line-height: 20px;
    display:inline-flex;
    align-items:center;
    gap:6px;
    user-select:none;
  }

  .rs-muted { font-size: 12px; }
`;

const STATUS_TO_ENUM = {
  Available: 0,
  Occupied: 1,
  Maintenance: 2,
  Reserved: 3,
  UnderMaintenance: 4,
  Cleaning: 5,
  Dirty: 6,
  OutOfService: 7,
  Unknown: 9,
};

const ENUM_TO_STATUS = {
  0: "Available",
  1: "Occupied",
  2: "Maintenance",
  3: "Reserved",
  4: "UnderMaintenance",
  5: "Cleaning",
  6: "Dirty",
  7: "OutOfService",
  9: "Unknown",
};

const pickApiMessage = (err, fallback) => {
  const d = err?.response?.data;
  return d?.message || d?.Message || d?.error || d?.Error || fallback;
};

const RoomStatusPage = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);

  // maintenance/out-of-service reason modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [maintenanceReason, setMaintenanceReason] = useState("");
  const [updating, setUpdating] = useState(false);

  // inject slim styles once
  useEffect(() => {
    const el = document.createElement("style");
    el.innerHTML = slimStyles;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  const normalizeStatus = useCallback((status) => {
    // Accept: enum int, numeric string, or name string
    if (typeof status === "number" && ENUM_TO_STATUS[status]) return ENUM_TO_STATUS[status];

    const sRaw = status ?? "";
    const s = String(sRaw).trim();

    // numeric string like "6"
    const n = Number(s);
    if (!Number.isNaN(n) && ENUM_TO_STATUS[n]) return ENUM_TO_STATUS[n];

    // synonyms / legacy
    const statusMap = {
      UnderMaintenance: "Maintenance",
      OutOfOrder: "OutOfService",
      Unavailable: "OutOfService",
      "": "Unknown",
      null: "Unknown",
      undefined: "Unknown",
    };

    return statusMap[s] || s;
  }, []);

  const getStatusIcon = useCallback((status) => {
    const icons = {
      Available: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      Occupied: <CloseOutlined style={{ color: "#f5222d" }} />,
      Cleaning: <SyncOutlined spin style={{ color: "#1890ff" }} />,
      Reserved: <StopOutlined style={{ color: "#faad14" }} />,
      Maintenance: <ToolOutlined style={{ color: "#722ed1" }} />,
      Dirty: <WarningOutlined style={{ color: "#fa8c16" }} />,
      OutOfService: <StopOutlined style={{ color: "#eb2f96" }} />,
      Unknown: <QuestionOutlined />,
    };
    return icons[status] || <QuestionOutlined />;
  }, []);

  const getStatusColor = useCallback((status) => {
    const colors = {
      Available: "green",
      Occupied: "red",
      Cleaning: "blue",
      Reserved: "orange",
      Maintenance: "purple",
      Dirty: "volcano",
      OutOfService: "magenta",
      Unknown: "default",
    };
    return colors[status] || "default";
  }, []);

  const getAvailableStatusTransitions = useCallback((currentStatus) => {
    const transitions = {
      Available: ["Occupied", "Reserved", "Maintenance", "Dirty"],
      Occupied: ["Cleaning", "Dirty"],
      Cleaning: ["Available", "Dirty"],
      Reserved: ["Occupied", "Available"],
      Maintenance: ["Available", "OutOfService"],
      Dirty: ["Cleaning", "OutOfService"],
      OutOfService: ["Maintenance", "Available"],
      Unknown: ["Available"],
    };
    return transitions[currentStatus] || [];
  }, []);

  const fetchRoomStatuses = useCallback(async () => {
    if (!hotelId) return;

    try {
      setLoading(true);
      const res = await axiosInstance.get(`/RoomStatus/Hotel/${hotelId}`);
      const list = Array.isArray(res.data) ? res.data : [];
      setRooms(list);
      setError(null);
    } catch (err) {
      console.error("Error fetching room statuses:", err);
      if (err.response?.status === 401) setError("You are not authorized. Please log in again.");
      else if (err.code === "ECONNABORTED") setError("Request timed out. Please try again.");
      else setError(pickApiMessage(err, "Failed to load room statuses. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    if (hotelId) fetchRoomStatuses();
  }, [hotelId, fetchRoomStatuses]);

  const filteredRooms = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rooms;

    return rooms.filter((room) =>
      Object.values(room).some((value) => String(value ?? "").toLowerCase().includes(q))
    );
  }, [rooms, searchQuery]);

  const statusCounts = useMemo(() => {
    const counts = {
      total: rooms.length,
      Available: 0,
      Occupied: 0,
      Cleaning: 0,
      Reserved: 0,
      Maintenance: 0,
      Dirty: 0,
      OutOfService: 0,
      Unknown: 0,
    };

    rooms.forEach((r) => {
      const s = normalizeStatus(r?.status);
      if (counts[s] != null) counts[s] += 1;
      else counts.Unknown += 1;
    });

    return counts;
  }, [rooms, normalizeStatus]);

  /**
   * IMPORTANT: we update using YOUR existing backend endpoint:
   * PUT /api/Rooms/UpdateStatus/{roomId}
   * body: { newStatus: <int enum>, reason?: <string> }
   */
  const putStatusChange = useCallback(
    async (room, nextStatus, reason) => {
      if (!room?.roomId || !nextStatus) return;

      const roomId = room.roomId;

      // Normalize UI status to enum
      const enumValue = STATUS_TO_ENUM[nextStatus];
      if (enumValue == null) {
        message.error("Invalid status value.");
        return;
      }

      // For Maintenance / OutOfService require reason (UI policy)
      if ((nextStatus === "Maintenance" || nextStatus === "OutOfService") && !String(reason || "").trim()) {
        message.error("Please provide a reason.");
        return;
      }

      try {
        setUpdating(true);

        const res = await axiosInstance.put(`/Rooms/UpdateStatus/${roomId}`, {
          newStatus: enumValue,
          reason: String(reason || "").trim() || null, // backend may ignore unless you add it to DTO
        });

        // Your controller returns NoContent() => HTTP 204
        if (res?.status === 204 || res?.status === 200) {
          notification.success({
            title: "Status Updated",
            description: `Room ${room.roomNumber ?? roomId} is now ${nextStatus}.`,
            placement: "topRight",
          });

          setStatusModalOpen(false);
          setSelectedRoom(null);
          setNewStatus(null);
          setMaintenanceReason("");

          await fetchRoomStatuses();
          return;
        }

        // Fallback
        notification.success({
          title: "Status Updated",
          description: `Room ${room.roomNumber ?? roomId} updated.`,
          placement: "topRight",
        });
        await fetchRoomStatuses();
      } catch (err) {
        console.error("Error updating room status:", err);

        const apiMsg = pickApiMessage(err, "Failed to update room status.");
        const status = err.response?.status;

        if (status === 400) {
          notification.warning({
            title: "Cannot Update Status",
            description: apiMsg,
            placement: "topRight",
            duration: 5,
          });
        } else if (status === 404) {
          notification.error({
            title: "Room Not Found",
            description: apiMsg,
            placement: "topRight",
          });
        } else if (status === 405) {
          notification.error({
            title: "Method Not Allowed (405)",
            description:
              "Your API route does not accept this HTTP method. Confirm the backend supports PUT /api/Rooms/UpdateStatus/{roomId}.",
            placement: "topRight",
            duration: 6,
          });
        } else {
          notification.error({
            title: "Update Failed",
            description: apiMsg,
            placement: "topRight",
          });
        }
      } finally {
        setUpdating(false);
      }
    },
    [fetchRoomStatuses]
  );

  const requestStatusChange = useCallback(
    (room, nextStatus) => {
      if (nextStatus === "Maintenance" || nextStatus === "OutOfService") {
        setSelectedRoom(room);
        setNewStatus(nextStatus);
        setMaintenanceReason("");
        setStatusModalOpen(true);
        return;
      }

      Modal.confirm({
        title: `Change status to ${nextStatus}?`,
        content: `Room ${room.roomNumber} will be set to ${nextStatus}.`,
        okText: "Confirm",
        cancelText: "Cancel",
        onOk: () => putStatusChange(room, nextStatus),
      });
    },
    [putStatusChange]
  );

  const columns = useMemo(() => {
    return [
      {
        title: "Room",
        dataIndex: "roomNumber",
        key: "roomNumber",
        width: 120,
        sorter: (a, b) => String(a.roomNumber || "").localeCompare(String(b.roomNumber || "")),
        render: (v, r) => (
          <Space size={6}>
            <Tag className="rs-pill" color="blue">
              #{r?.roomId}
            </Tag>
            <span title={v}>{v || "N/A"}</span>
          </Space>
        ),
      },
      {
        title: "Type",
        dataIndex: "roomTypeName",
        key: "roomTypeName",
        width: 180,
        ellipsis: true,
        sorter: (a, b) => String(a.roomTypeName || "").localeCompare(String(b.roomTypeName || "")),
        render: (v) => <span title={v}>{v || "—"}</span>,
      },
      {
        title: "Floor",
        dataIndex: "floor",
        key: "floor",
        width: 110,
        sorter: (a, b) => String(a.floor || "").localeCompare(String(b.floor || "")),
        render: (v) => v ?? "—",
      },
      {
        title: "Check Out",
        dataIndex: "checkOutDate",
        key: "checkOutDate",
        width: 140,
        sorter: (a, b) => new Date(a.checkOutDate || 0) - new Date(b.checkOutDate || 0),
        render: (v) => (v ? new Date(v).toLocaleDateString() : "—"),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 170,
        filters: [
          "Available",
          "Occupied",
          "Cleaning",
          "Reserved",
          "Maintenance",
          "Dirty",
          "OutOfService",
          "Unknown",
        ].map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => normalizeStatus(record.status) === value,
        render: (status, record) => {
          const current = normalizeStatus(status);
          const transitions = getAvailableStatusTransitions(current);

          const items = transitions.map((s) => ({
            key: s,
            icon: getStatusIcon(s),
            label: s,
          }));

          const clickable = items.length > 0;

          const tagNode = (
            <Tag
              className="rs-pill"
              color={getStatusColor(current)}
              style={{
                cursor: clickable ? "pointer" : "not-allowed",
                opacity: clickable ? 1 : 0.7,
              }}
            >
              {getStatusIcon(current)}
              <span>{current}</span>
              {clickable && <DownOutlined style={{ fontSize: 10 }} />}
            </Tag>
          );

          if (!clickable) return tagNode;

          return (
            <Dropdown
              trigger={["click"]}
              menu={{
                items,
                onClick: ({ key }) => requestStatusChange(record, String(key)),
              }}
            >
              {tagNode}
            </Dropdown>
          );
        },
      },
    ];
  }, [
    normalizeStatus,
    getAvailableStatusTransitions,
    getStatusIcon,
    getStatusColor,
    requestStatusChange,
  ]);

  return (
    <div className="rs-page">
      <Breadcrumb />

      {/* bordered -> variant (fix warning) */}
      <Card className="rs-card" variant="outlined" styles={{ body: { padding: 12 } }}>
        <div className="rs-head">
          <div className="rs-head-title">
            <Space size={8} align="center">
              <HomeOutlined />
              <Title level={5} style={{ margin: 0 }}>
                Room Status
              </Title>
              <Badge count={statusCounts.total} showZero />
            </Space>
            <Text type="secondary" className="rs-muted">
              Tap a status pill to change the room status using valid transitions.
            </Text>
          </div>

          <div className="rs-actions">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchRoomStatuses}
              loading={loading}
            >
              Refresh
            </Button>
          </div>
        </div>

        <Row gutter={12} className="rs-filters">
          <Col xs={24} sm={14} md={10} lg={8}>
            <Input
              placeholder="Search rooms…"
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={10} md={14} lg={16}>
            <Space wrap size={8}>
              <Tag className="rs-pill" color="green">Available: {statusCounts.Available}</Tag>
              <Tag className="rs-pill" color="red">Occupied: {statusCounts.Occupied}</Tag>
              <Tag className="rs-pill" color="blue">Cleaning: {statusCounts.Cleaning}</Tag>
              <Tag className="rs-pill" color="orange">Reserved: {statusCounts.Reserved}</Tag>
              <Tag className="rs-pill" color="purple">Maintenance: {statusCounts.Maintenance}</Tag>
              <Tag className="rs-pill" color="volcano">Dirty: {statusCounts.Dirty}</Tag>
              <Tag className="rs-pill" color="magenta">OutOfService: {statusCounts.OutOfService}</Tag>
            </Space>
          </Col>
        </Row>

        {error && (
          <Alert
            style={{ marginTop: 12 }}
            message="Error"
            description={error}
            type="error"
            showIcon
          />
        )}

        <div style={{ height: 12 }} />

        <Table
          className="rs-table"
          size="small"
          columns={columns}
          dataSource={filteredRooms}
          rowKey="roomId"
          loading={loading}
          tableLayout="fixed"
          scroll={{ x: 900 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rooms`,
          }}
          locale={{
            emptyText: <Text type="secondary">No room statuses found</Text>,
          }}
        />

        <Modal
          title={
            selectedRoom
              ? `Set Room ${selectedRoom.roomNumber} to ${newStatus}`
              : "Set Status"
          }
          open={statusModalOpen}
          confirmLoading={updating}
          okText="Update"
          onOk={() => {
            if (!selectedRoom || !newStatus) return;
            putStatusChange(selectedRoom, newStatus, maintenanceReason);
          }}
          onCancel={() => {
            setStatusModalOpen(false);
            setSelectedRoom(null);
            setNewStatus(null);
            setMaintenanceReason("");
          }}
          destroyOnHidden
        >
          <Text type="secondary" className="rs-muted">
            Please provide a short reason (this will be logged).
          </Text>
          <div style={{ height: 8 }} />
          <Input.TextArea
            placeholder={`Reason for ${newStatus || "status"}...`}
            value={maintenanceReason}
            onChange={(e) => setMaintenanceReason(e.target.value)}
            rows={4}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default RoomStatusPage;
